import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import mixpanel from 'mixpanel-browser'

import { useOnboardingStore } from '../../store/onboarding-store'

import Button from '../../common/components/Button'
import Layout from '../../common/components/Layout'
import styles from './Question.module.css'
import Slider from '../../common/components/Slider'
import ProgressBar from '../../components/Question/ProgressBar'
import { RouterParams } from '../../types/RouterParams'
import {
  OnboardingQuestion,
  QuestionType,
} from '../../common/types/onboarding-questions'
import { authFetch } from '../../common/utils/cognito'
import { t } from 'i18next'
import { getDetectedLanguage } from '../../i18n'

const IN_RELATIONSHIP_QUESTION_ID = 9
const RELATIONSHIP_QUESTION_ID = 10

const QuestionPage = () => {
  const { id } = useParams<RouterParams>()
  const [onboardingQuestion, setOnboardingQuestion] =
    useState<OnboardingQuestion>()
  const [isInRelationship, setIsInRelationship] = useState<boolean>(false)
  const [currentAnswer, setCurrentAnswer] = useState<number | boolean>(0)
  const navigate = useNavigate()
  const {
    fetchQuestions,
    questions,
    responses,
    hasStarted,
    getAnswer,
    setAnswer,
    setRecommendations,
    setHighRisk,
  } = useOnboardingStore()

  const sliderLabels = [
    t('onboarding.slider.0'),
    t('onboarding.slider.1'),
    t('onboarding.slider.2'),
    t('onboarding.slider.3'),
  ]

  useEffect(() => {
    fetchQuestions()
  }, [fetchQuestions])

  useEffect(() => {
    const question = questions.find(
      ({ id: questionId }) => questionId === Number(id),
    )

    if (!question) {
      return
    }

    mixpanel.track('Onboarding Questionnaire', {
      question: id,
    })

    if (question.id === RELATIONSHIP_QUESTION_ID) {
      if (isInRelationship) {
        setOnboardingQuestion({
          ...question,
          question: {
            de: question.question.de.replace(
              '{relationship_type}',
              'meiner Beziehung',
            ),
            en: question.question.en.replace(
              '{relationship_type}',
              'my relationship',
            ),
          },
        })
      } else {
        setOnboardingQuestion({
          ...question,
          question: {
            de: question.question.de.replace(
              '{relationship_type}',
              'meiner Singleleben',
            ),
            en: question.question.en.replace(
              '{relationship_type}',
              'being single',
            ),
          },
        })
      }
    } else {
      setOnboardingQuestion(question)
    }

    setCurrentAnswer(getAnswer(question.id) || question.defaultValue)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, questions])

  if (!id || !onboardingQuestion) return null

  const handleNextClick = async () => {
    setAnswer(onboardingQuestion.id, currentAnswer)

    if (+id < questions.length) {
      navigate(`/question/${Number(id) + 1}`)
    } else if (+id === questions.length) {
      const response = await authFetch(
        `${process.env.REACT_APP_API_URL}/user-onboarding`,
        {
          method: hasStarted ? 'PATCH' : 'POST',
          body: JSON.stringify({ responses }),
        },
      )

      const { highRisk, recommendedTrackIds } = await response.json()
      setRecommendations(recommendedTrackIds)
      setHighRisk(highRisk)
      mixpanel.track('Onboarding Questionnaire Completed')
      navigate('/priorities')
    }
  }

  const onChange = (value: number | boolean) => {
    setCurrentAnswer(value)

    if (onboardingQuestion.id === IN_RELATIONSHIP_QUESTION_ID) {
      setIsInRelationship(Boolean(value))
    }
  }

  const onSkipClick = () => {
    mixpanel.track('Onboarding Questionnaire Skipped', {
      question: onboardingQuestion.id,
    })
  }

  return (
    <Layout hasBackButton contentStyles={{ display: 'flex' }}>
      <div className={styles.questionContainer}>
        <div>
          <ProgressBar currentStep={+id} totalSteps={15} />
          <h3 className={styles.subtitle}>{t('onboarding.title')}</h3>
          <span>{t('onboarding.header')}</span>
          <p>{onboardingQuestion.question[getDetectedLanguage()]}</p>
        </div>
        <div className={styles.actionContainer}>
          {onboardingQuestion.type === QuestionType.Scale && (
            <Slider
              labels={sliderLabels}
              onChange={onChange}
              sliderValue={Number(currentAnswer)}
            />
          )}
          {onboardingQuestion.type === QuestionType.YesNo && (
            <div className={styles.buttonContainer}>
              <Button
                title={t('onboarding.no')}
                onClick={() => onChange(false)}
                backgroundColor={
                  !Boolean(currentAnswer) ? undefined : 'decline'
                }
                size="half"
              />
              <Button
                title={t('onboarding.yes')}
                onClick={() => onChange(true)}
                backgroundColor={Boolean(currentAnswer) ? undefined : 'decline'}
                size="half"
              />
            </div>
          )}
          <div className={styles.nextButtonContainer}>
            <Button title={t('onboarding.next')} onClick={handleNextClick} />
          </div>
          <Link to="/feed" onClick={onSkipClick} className={styles.skip}>
            {t('onboarding.skip')}
          </Link>
        </div>
      </div>
    </Layout>
  )
}

export default QuestionPage
